import Dexie, { type EntityTable } from 'dexie'

import type { DiarioEntrada } from '@/tipos/tabelas'

/**
 * Fila de escrita offline.
 *
 * O diário é o diferencial declarado do programa (seção 10) e é
 * preenchido no fim do dia, muitas vezes sem sinal. Por isso a resposta
 * grava primeiro aqui e só depois sobe: a tela sempre lê do local, e a
 * sincronização é assunto de segundo plano.
 */

export interface DiarioLocal {
  /** `${matriculaId}:${data}` — mesma chave do UNIQUE no Postgres. */
  chave: string
  matriculaId: string
  data: string
  diaNumero: number
  entrada: DiarioEntrada
  atualizadoEm: number
  /** `pendente` enquanto não subiu; `sincronizado` depois do upsert. */
  estado: 'pendente' | 'sincronizado'
  /** Quantas vezes o envio falhou. Usado para espaçar as tentativas. */
  tentativas: number
  /** `null` quando não há falha. Não `undefined`: o Dexie não aceita
   *  undefined num update, e o campo precisa poder ser limpo. */
  ultimoErro: string | null
}

export interface ItemListaLocal {
  id: string
  listaId: string
  comprado: boolean
  atualizadoEm: number
  estado: 'pendente' | 'sincronizado'
}

const bancoLocal = new Dexie('viver') as Dexie & {
  diarios: EntityTable<DiarioLocal, 'chave'>
  itensLista: EntityTable<ItemListaLocal, 'id'>
}

bancoLocal.version(1).stores({
  diarios: 'chave, matriculaId, data, estado',
  itensLista: 'id, listaId, estado',
})

export { bancoLocal }

export function chaveDiario(matriculaId: string, data: string) {
  return `${matriculaId}:${data}`
}

/** Grava o diário localmente e o marca como pendente de envio. */
export async function salvarDiarioLocal(
  matriculaId: string,
  data: string,
  diaNumero: number,
  entrada: DiarioEntrada,
) {
  const chave = chaveDiario(matriculaId, data)
  await bancoLocal.diarios.put({
    chave,
    matriculaId,
    data,
    diaNumero,
    entrada,
    atualizadoEm: Date.now(),
    estado: 'pendente',
    tentativas: 0,
    ultimoErro: null,
  })
  return chave
}

export async function lerDiarioLocal(matriculaId: string, data: string) {
  return bancoLocal.diarios.get(chaveDiario(matriculaId, data))
}

export async function diariosPendentes() {
  return bancoLocal.diarios.where('estado').equals('pendente').toArray()
}

export async function marcarSincronizado(chave: string) {
  await bancoLocal.diarios.update(chave, { estado: 'sincronizado', ultimoErro: null })
}

export async function registrarFalha(chave: string, erro: string) {
  const atual = await bancoLocal.diarios.get(chave)
  if (!atual) return
  await bancoLocal.diarios.update(chave, {
    tentativas: atual.tentativas + 1,
    ultimoErro: erro,
  })
}

/**
 * Pede ao navegador para não despejar o IndexedDB sob pressão de espaço.
 *
 * É um pedido, não uma garantia — o Safari pode recusar. Vale chamar
 * mesmo assim: quando aceito, protege 28 dias de registro.
 */
export async function pedirArmazenamentoPersistente() {
  if (!navigator.storage?.persist) return false
  if (await navigator.storage.persisted()) return true
  return navigator.storage.persist()
}
